// Lets any page publish a human-readable label for the current (leaf) route
// segment — e.g. a horse or sire name — so breadcrumbs can show "Written
// Tycoon" instead of "Sire #837693". Pages that don't opt in simply fall back
// to the route's default label. This is intentionally lightweight: only the
// current page's leaf label is tracked, and it clears automatically on
// navigation.
import React, { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

export interface BreadcrumbContextValue {
  leafLabel: string | null;
  setLeafLabel: (label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue>({
  leafLabel: null,
  setLeafLabel: () => {},
});

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  const [leafLabel, setLeafLabel] = useState<string | null>(null);
  return (
    <BreadcrumbContext.Provider value={{ leafLabel, setLeafLabel }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

export function useBreadcrumbLeaf(): string | null {
  return useContext(BreadcrumbContext).leafLabel;
}

// Call from a detail page to name its leaf crumb. Pass a falsy value while the
// name is still loading and the route default will be shown instead.
export function useSetBreadcrumb(label: string | null | undefined): void {
  const { setLeafLabel } = useContext(BreadcrumbContext);
  useEffect(() => {
    setLeafLabel(label || null);
    return () => setLeafLabel(null);
  }, [label, setLeafLabel]);
}

export default BreadcrumbContext;
